import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { BOARD_TOP_Y, MARBLE_RADIUS, WELL_RADIUS, boardToXZ } from './constants'
import type { Position } from '../../stores/gameStore'

const MARKER_Y = BOARD_TOP_Y + 0.014
const GOLD = '#ffd98a'

interface HighlightsProps {
  selected: Position | null
  targets: Position[]
}

function TargetMarker({ position, phase }: { position: Position; phase: number }) {
  const ringRef = useRef<THREE.MeshBasicMaterial>(null)
  const dotRef = useRef<THREE.MeshBasicMaterial>(null)
  const { x, z } = boardToXZ(position)

  useFrame((state) => {
    const pulse = 0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 3 + phase)
    if (ringRef.current) ringRef.current.opacity = 0.4 + pulse * 0.4
    if (dotRef.current) dotRef.current.opacity = 0.14 + pulse * 0.16
  })

  return (
    <group position={[x, MARKER_Y, z]} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh>
        <ringGeometry args={[WELL_RADIUS * 0.66, WELL_RADIUS * 0.9, 40]} />
        <meshBasicMaterial
          ref={ringRef}
          color={GOLD}
          transparent
          opacity={0.6}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      <mesh position={[0, 0, -0.001]}>
        <circleGeometry args={[WELL_RADIUS * 0.6, 36]} />
        <meshBasicMaterial
          ref={dotRef}
          color={GOLD}
          transparent
          opacity={0.2}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  )
}

function SelectMarker({ position }: { position: Position }) {
  const matRef = useRef<THREE.MeshBasicMaterial>(null)
  const { x, z } = boardToXZ(position)

  useFrame((state) => {
    const pulse = 0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 4)
    if (matRef.current) matRef.current.opacity = 0.55 + pulse * 0.4
  })

  return (
    <mesh position={[x, MARKER_Y, z]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[MARBLE_RADIUS * 1.02, MARBLE_RADIUS * 1.22, 48]} />
      <meshBasicMaterial
        ref={matRef}
        color={GOLD}
        transparent
        opacity={0.8}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  )
}

export default function Highlights({ selected, targets }: HighlightsProps) {
  return (
    <group>
      {selected && <SelectMarker position={selected} />}
      {targets.map((target, index) => (
        <TargetMarker key={`${target.q},${target.r}`} position={target} phase={index * 0.7} />
      ))}
    </group>
  )
}
